import { useState, useEffect, useCallback } from 'react'
import { fetchOpsStatus, fetchOpsLogs } from '../services/api'
import AnimatedNumber from '../components/AnimatedNumber'
import { safeArray, safeEntries } from '../utils/safe'

// ─── Fallback dummy data when API is unreachable ───
const FALLBACK_DATA = {
  server: {
    hostname: 'vps-7b54c6d8',
    cpu_percent: 23,
    memory: { percent: 45, display: '3.6GB / 8GB' },
    disk: { percent: 62, display: '62GB / 100GB' },
    uptime: '14 يوم 7 ساعة',
    load_avg: '0.45, 0.38, 0.32'
  },
  processes: [
    { name: 'rafiq-backend', status: 'healthy', uptime: '14d', cpu: 2.1, memory_mb: 128, restarts: 0, pid: 1234 },
    { name: 'muwafaq-backend', status: 'healthy', uptime: '12d', cpu: 1.8, memory_mb: 96, restarts: 1, pid: 2345 },
    { name: 'sahha-backend', status: 'healthy', uptime: '10d', cpu: 3.2, memory_mb: 112, restarts: 0, pid: 3456 },
    { name: 'labor-backend', status: 'healthy', uptime: '5d', cpu: 1.5, memory_mb: 88, restarts: 0, pid: 4567 },
  ],
  security: {
    fail2ban: { total_bans: 47, currently_banned: 3, jails: ['sshd'], recent_bans: [
      { ip: '185.220.xx.xx', time: '08:12' },
      { ip: '194.26.xx.xx', time: '07:45' },
      { ip: '45.148.xx.xx', time: '06:30' },
    ]},
    ufw: { active: true, rules_count: 8, rules: ['22/tcp ALLOW', '80/tcp ALLOW', '443/tcp ALLOW'] },
    ssl: { status: 'valid', expiry: '2026-07-12', days_left: 88, domain: '*.sanajim.com' }
  },
  nginx: { total_today: 12847, by_status: { '200': 11203, '304': 987, '404': 412, '500': 245 }, by_method: { GET: 9823, POST: 2415, PUT: 412, DELETE: 197 } }
}

const FALLBACK_LOGS = [
  { method: 'GET', path: '/api/v1/projects', status: 200, timestamp: '08:39:12', user_agent: 'Mozilla/5.0' },
  { method: 'POST', path: '/api/v1/auth/login', status: 200, timestamp: '08:38:45', user_agent: 'Mozilla/5.0' },
  { method: 'GET', path: '/api/v1/analytics', status: 200, timestamp: '08:38:22', user_agent: 'Mozilla/5.0' },
  { method: 'GET', path: '/api/v1/ops/status', status: 200, timestamp: '08:37:58', user_agent: 'Mozilla/5.0' },
  { method: 'POST', path: '/api/v1/evidence/upload', status: 200, timestamp: '08:37:30', user_agent: 'Mozilla/5.0' },
  { method: 'GET', path: '/api/v1/claims', status: 200, timestamp: '08:36:55', user_agent: 'Mozilla/5.0' },
  { method: 'PUT', path: '/api/v1/projects/rafiq', status: 200, timestamp: '08:36:20', user_agent: 'Mozilla/5.0' },
  { method: 'GET', path: '/api/v1/security/status', status: 200, timestamp: '08:35:48', user_agent: 'Mozilla/5.0' },
  { method: 'DELETE', path: '/api/v1/logs/archive', status: 200, timestamp: '08:35:12', user_agent: 'Mozilla/5.0' },
  { method: 'GET', path: '/api/v1/dashboard', status: 200, timestamp: '08:34:45', user_agent: 'Mozilla/5.0' },
  { method: 'POST', path: '/api/v1/claims/submit', status: 201, timestamp: '08:34:10', user_agent: 'Mozilla/5.0' },
  { method: 'GET', path: '/api/v1/health', status: 200, timestamp: '08:33:50', user_agent: 'Node/22.22' },
]

// ─── Ops Config ───
const OPS_API = 'https://dashboard.sanajim.com/ops-api'

const SERVICE_COLORS = {
  rafiq: { primary: '#00BFA5', glow: 'rgba(0,191,165,0.15)' },
  muwafaq: { primary: '#4CAF50', glow: 'rgba(76,175,80,0.15)' },
  sahha: { primary: '#448AFF', glow: 'rgba(68,138,255,0.15)' },
}

const STATUS_COLORS = {
  healthy: '#22C55E',
  online: '#22C55E',
  degraded: '#F59E0B',
  down: '#EF4444',
  stopped: '#EF4444',
}

const STATUS_LABELS = {
  healthy: 'يعمل',
  online: 'يعمل',
  degraded: 'بطيء',
  down: 'متوقف',
  stopped: 'متوقف',
}

const HTTP_METHOD_COLORS = {
  GET: '#22C55E',
  POST: '#3B82F6',
  PUT: '#F59E0B',
  PATCH: '#8B5CF6',
  DELETE: '#EF4444',
}

function statusColor(s) { return STATUS_COLORS[s] || '#94A3B8' }
function statusLabel(s) { return STATUS_LABELS[s] || s }
function methodColor(m) { return HTTP_METHOD_COLORS[m] || '#94A3B8' }

export default function Operations() {
  const [data, setData] = useState(null)
  const [logs, setLogs] = useState([])
  const [logFilter, setLogFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [lastRefresh, setLastRefresh] = useState(null)
  const [activeSection, setActiveSection] = useState('all')

  const loadData = useCallback(async () => {
    try {
      const [statusRes, logsRes] = await Promise.all([
        fetchOpsStatus(),
        fetchOpsLogs(logFilter === 'all' ? 60 : 60, logFilter),
      ])
      setData(statusRes)
      setLogs(safeArray(logsRes?.entries))
      setLastRefresh(new Date())
    } catch {
      // API unreachable — use fallback dummy data
      setData(FALLBACK_DATA)
      setLogs(FALLBACK_LOGS)
      setLastRefresh(new Date())
    } finally {
      setLoading(false)
    }
  }, [logFilter])

  useEffect(() => {
    loadData()
    const interval = setInterval(loadData, 30000)
    return () => clearInterval(interval)
  }, [loadData])

  const timeStr = lastRefresh
    ? lastRefresh.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '--:--'

  const sections = [
    { key: 'all', label: 'الكل', icon: '◈' },
    { key: 'server', label: 'السيرفر', icon: '🖥️' },
    { key: 'services', label: 'الخدمات', icon: '⚡' },
    { key: 'logs', label: 'السجلات', icon: '📊' },
    { key: 'security', label: 'الأمان', icon: '🔐' },
  ]

  const show = (key) => activeSection === 'all' || activeSection === key

  if (loading && !data) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-surface border border-white/[0.06] flex items-center justify-center text-lg">🦅</div>
          <div>
            <div className="h-5 w-48 bg-surface rounded shimmer-bg" />
            <div className="h-3 w-32 bg-surface rounded shimmer-bg mt-2" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="glass rounded-card p-5 h-40 shimmer-bg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* ─── Header ─── */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6]/20 to-[#F59E0B]/20 border border-white/[0.08] flex items-center justify-center text-lg">
            🦅
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#F1F5F9]">مركز العمليات</h1>
            <p className="text-[11px] text-[#64748B]">سناجم تكنولوجيا — القيادة الفعلية</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#475569]">⏱ {timeStr}</span>
          <button onClick={loadData}
            className="px-3 py-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-[#64748B] hover:text-[#F1F5F9] text-xs font-medium transition-all duration-150 active:scale-95">
            تحديث
          </button>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-health-pulse" />
            مباشر
          </span>
        </div>
      </div>

      {/* ─── Section Filter ─── */}
      <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1 scrollbar-none">
        {sections.map(s => (
          <button key={s.key} onClick={() => setActiveSection(s.key)}
            className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-150 ${
              activeSection === s.key
                ? 'bg-white/[0.08] text-[#F1F5F9]'
                : 'text-[#64748B] hover:bg-white/[0.04] hover:text-[#94A3B8]'
            }`}>
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* ─── 1. Server Health ─── */}
      {/* ═══════════════════════════════════════════ */}
      {show('server') && (
        <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '0ms' }}>
          <SectionTitle icon="🖥️" title="صحة السيرفر" subtitle={data?.server?.hostname} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard
              label="المعالج" value={`${data?.server?.cpu_percent || 0}%`}
              icon="⚡" color="#8B5CF6"
              bar={data?.server?.cpu_percent || 0}
            />
            <MetricCard
              label="الذاكرة" value={data?.server?.memory?.display || '-'}
              icon="🧠" color="#3B82F6"
              bar={data?.server?.memory?.percent || 0}
            />
            <MetricCard
              label="القرص" value={data?.server?.disk?.display || '-'}
              icon="💾" color="#F59E0B"
              bar={data?.server?.disk?.percent || 0}
            />
            <MetricCard
              label="مدة التشغيل" value={data?.server?.uptime || '-'}
              icon="⏱" color="#22C55E"
            />
          </div>
          {data?.server?.load_avg && (
            <div className="mt-3 glass rounded-card-sm px-4 py-2.5 flex items-center gap-4 flex-wrap">
              <span className="text-[10px] text-[#64748B]">حميل النظام:</span>
              {data.server.load_avg.split(',').map((v, i) => (
                <span key={i} className="text-xs text-[#94A3B8] tabular-nums">
                  {['1m', '5m', '15m'][i]}: <span className="text-[#F1F5F9] font-medium">{v.trim()}</span>
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* ─── 2. Services Status ─── */}
      {/* ═══════════════════════════════════════════ */}
      {show('services') && (
        <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '60ms' }}>
          <SectionTitle icon="⚡" title="حالة الخدمات" subtitle={`${data?.processes?.length || 0} عمليات`} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(data?.processes || []).map(proc => {
              const key = proc.name.replace('-backend', '')
              const colors = SERVICE_COLORS[key] || { primary: '#8B5CF6', glow: 'rgba(139,92,246,0.15)' }
              return (
                <div key={proc.name} className="glass rounded-card p-4 relative overflow-hidden group">
                  <div className="absolute top-0 inset-x-0 h-[2px]" style={{ background: `linear-gradient(to left, ${colors.primary}, transparent)` }} />
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{key === 'rafiq' ? '🚗' : key === 'muwafaq' ? '✅' : '🏥'}</span>
                      <div>
                        <p className="text-sm font-bold text-[#F1F5F9]">{key === 'rafiq' ? 'رفيق' : key === 'muwafaq' ? 'موافق' : 'صحة'}</p>
                        <p className="text-[10px] text-[#475569]">{proc.name}</p>
                      </div>
                    </div>
                    <HealthDot status={proc.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <MiniStat label="التشغيل" value={proc.uptime} />
                    <MiniStat label="المعالج" value={`${proc.cpu}%`} />
                    <MiniStat label="الذاكرة" value={`${proc.memory_mb}MB`} />
                    <MiniStat label="إعادة التشغيل" value={proc.restarts} />
                  </div>
                  <p className="text-[10px] text-[#475569] mt-2 tabular-nums">PID: {proc.pid}</p>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* ─── 3. API Activity Log ─── */}
      {/* ═══════════════════════════════════════════ */}
      {show('logs') && (
        <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '120ms' }}>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <SectionTitle icon="📊" title="سجل الطلبات" subtitle={`${logs.length} طلب`} noMargin />
            <div className="flex gap-1 overflow-x-auto scrollbar-none">
              {[
                { key: 'all', label: 'الكل' },
                { key: 'errors', label: 'أخطاء' },
                { key: '4xx', label: '4xx' },
                { key: '5xx', label: '5xx' },
              ].map(f => (
                <button key={f.key} onClick={() => setLogFilter(f.key)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all whitespace-nowrap ${
                    logFilter === f.key
                      ? 'bg-white/[0.08] text-[#F1F5F9]'
                      : 'text-[#64748B] hover:bg-white/[0.04]'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="glass rounded-card overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-white/[0.06] text-[10px] font-semibold text-[#475569] uppercase">
              <div className="col-span-2">الطريقة</div>
              <div className="col-span-4">المسار</div>
              <div className="col-span-1">الحالة</div>
              <div className="col-span-2">التوقيت</div>
              <div className="col-span-3 hidden md:block">المتصفح</div>
            </div>
            {/* Rows */}
            <div className="max-h-[400px] overflow-y-auto">
              {logs.length === 0 ? (
                <div className="px-4 py-8 text-center text-xs text-[#475569]">لا توجد سجلات</div>
              ) : (
                logs.slice(0, 40).map((entry, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors text-xs">
                    <div className="col-span-2">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ color: methodColor(entry.method), backgroundColor: `${methodColor(entry.method)}15` }}>
                        {entry.method}
                      </span>
                    </div>
                    <div className="col-span-4 text-[#94A3B8] truncate tabular-nums font-mono text-[11px]">{entry.path}</div>
                    <div className="col-span-1">
                      <span className={`text-[10px] font-bold ${entry.status >= 500 ? 'text-red-400' : entry.status >= 400 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {entry.status}
                      </span>
                    </div>
                    <div className="col-span-2 text-[#475569] text-[10px] tabular-nums">{entry.timestamp}</div>
                    <div className="col-span-3 hidden md:block text-[#475569] text-[10px] truncate">{entry.user_agent}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* ─── 4. Security Monitor ─── */}
      {/* ═══════════════════════════════════════════ */}
      {show('security') && (
        <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '180ms' }}>
          <SectionTitle icon="🔐" title="مراقبة الأمان" subtitle="الحالة الأمنية" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* fail2ban */}
            <div className="glass rounded-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-base">🛡️</span>
                <p className="text-sm font-bold text-[#F1F5F9]">fail2ban</p>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <MiniStat label="الحظر الإجمالي" value={data?.security?.fail2ban?.total_bans || 0} color="#EF4444" />
                <MiniStat label="محظور حالياً" value={data?.security?.fail2ban?.currently_banned || 0} color="#F59E0B" />
              </div>
              {data?.security?.fail2ban?.jails?.length > 0 && (
                <p className="text-[10px] text-[#475569] mb-2">السجون: {safeArray(data?.security?.fail2ban?.jails).join(', ')}</p>
              )}
              {data?.security?.fail2ban?.recent_bans?.length > 0 && (
                <div className="mt-2">
                  <p className="text-[10px] font-semibold text-[#64748B] mb-1">آخر الحظر:</p>
                  <div className="max-h-[120px] overflow-y-auto">
                    {safeArray(data?.security?.fail2ban?.recent_bans).slice(0, 8).map((ban, i) => (
                      <div key={i} className="flex items-center justify-between py-1 border-b border-white/[0.03]">
                        <span className="text-[10px] text-red-400 tabular-nums font-mono">{ban.ip}</span>
                        <span className="text-[10px] text-[#475569]">{ban.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* UFW */}
            <div className="glass rounded-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-base">🧱</span>
                <p className="text-sm font-bold text-[#F1F5F9]">جدار الحماية</p>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <HealthDot status={data?.security?.ufw?.active ? 'online' : 'down'} />
                <span className="text-xs font-medium" style={{ color: data?.security?.ufw?.active ? '#22C55E' : '#EF4444' }}>
                  {data?.security?.ufw?.active ? 'مفعّل' : 'غير مفعّل'}
                </span>
              </div>
              <MiniStat label="القواعد" value={data?.security?.ufw?.rules_count || 0} />
              {data?.security?.ufw?.rules?.length > 0 && (
                <div className="mt-2 max-h-[120px] overflow-y-auto">
                  {safeArray(data?.security?.ufw?.rules).map((rule, i) => (
                    <p key={i} className="text-[10px] text-[#94A3B8] font-mono tabular-nums py-0.5 truncate">{rule}</p>
                  ))}
                </div>
              )}
            </div>

            {/* SSL */}
            <div className="glass rounded-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-base">🔒</span>
                <p className="text-sm font-bold text-[#F1F5F9]">شهادة SSL</p>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <HealthDot status={data?.security?.ssl?.status === 'valid' ? 'online' : data?.security?.ssl?.status === 'warning' ? 'degraded' : 'down'} />
                <span className="text-xs font-medium" style={{ color: data?.security?.ssl?.status === 'valid' ? '#22C55E' : '#F59E0B' }}>
                  {data?.security?.ssl?.status === 'valid' ? 'صالحة' : 'تحتاج تجديد'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <MiniStat label="ينتهي" value={data?.security?.ssl?.expiry || '-'} />
                <MiniStat label="المتبقي" value={`${data?.security?.ssl?.days_left || 0} يوم`} color={data?.security?.ssl?.days_left < 30 ? '#EF4444' : '#22C55E'} />
              </div>
              <p className="text-[10px] text-[#475569] mt-2 tabular-nums">{data?.security?.ssl?.domain}</p>
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* ─── 5. Nginx Stats ─── */}
      {/* ═══════════════════════════════════════════ */}
      {show('server') && data?.nginx && (
        <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '240ms' }}>
          <SectionTitle icon="🌐" title="إحصائيات الطلبات" subtitle={`${data?.nginx?.total_today || 0} اليوم`} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="طلبات اليوم" value={data?.nginx?.total_today || 0} icon="📨" color="#8B5CF6" />
            {safeEntries(data?.nginx?.by_status).slice(0, 3).map(([code, count]) => (
              <MetricCard key={code}
                label={`HTTP ${code}`}
                value={count}
                icon={parseInt(code) >= 500 ? '🔴' : parseInt(code) >= 400 ? '🟡' : '🟢'}
                color={parseInt(code) >= 500 ? '#EF4444' : parseInt(code) >= 400 ? '#F59E0B' : '#22C55E'}
              />
            ))}
          </div>
          {data?.nginx?.by_method && Object.keys(data.nginx.by_method).length > 0 && (
            <div className="mt-3 glass rounded-card-sm px-4 py-2.5 flex items-center gap-4 flex-wrap">
              <span className="text-[10px] text-[#64748B]">بالطريقة:</span>
              {safeEntries(data?.nginx?.by_method).map(([method, count]) => (
                <span key={method} className="text-xs tabular-nums">
                  <span className="font-bold" style={{ color: methodColor(method) }}>{method}</span>
                  <span className="text-[#94A3B8] ml-1">{count.toLocaleString()}</span>
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ─── Footer ─── */}
      <div className="text-center py-4">
        <p className="text-[10px] text-[#475569]">يتم التحديث كل ٣٠ ثانية — آخر تحديث: {timeStr}</p>
        <p className="text-[10px] text-[#475569] mt-1">© 2026 سناجم تكنولوجيا — جميع الحقوق محفوظة</p>
      </div>
    </div>
  )
}

// ─── Helper Components ───

function SectionTitle({ icon, title, subtitle, noMargin }) {
  return (
    <div className={`flex items-center gap-2 ${noMargin ? '' : 'mb-3'}`}>
      <span className="text-lg">{icon}</span>
      <h2 className="text-base font-bold text-[#F1F5F9]">{title}</h2>
      {subtitle && <span className="text-[10px] text-[#475569]">— {subtitle}</span>}
    </div>
  )
}

function MetricCard({ label, value, icon, color, bar }) {
  return (
    <div className="glass rounded-card p-4 relative overflow-hidden">
      {bar !== undefined && (
        <div className="absolute bottom-0 inset-x-0 h-1 bg-white/[0.04]">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(bar, 100)}%`, backgroundColor: color }} />
        </div>
      )}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm">{icon}</span>
        <p className="text-[10px] font-medium text-[#64748B]">{label}</p>
      </div>
      <p className="text-xl font-extrabold tabular-nums" style={{ color }}>
        {typeof value === 'number' ? <AnimatedNumber value={value} duration={400} /> : value}
      </p>
    </div>
  )
}

function MiniStat({ label, value, color }) {
  return (
    <div className="rounded-[8px] bg-base border border-white/[0.04] px-3 py-2">
      <p className="text-[10px] text-[#64748B] mb-0.5">{label}</p>
      <p className="text-sm font-bold tabular-nums" style={{ color: color || '#F1F5F9' }}>
        {typeof value === 'number' ? <AnimatedNumber value={value} duration={300} /> : value}
      </p>
    </div>
  )
}

function HealthDot({ status }) {
  const color = statusColor(status)
  return (
    <div className="flex items-center gap-1.5">
      <div className="relative">
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
        {(status === 'healthy' || status === 'online') && (
          <div className="absolute inset-0 w-2.5 h-2.5 rounded-full animate-health-glow" style={{ backgroundColor: color, opacity: 0.35 }} />
        )}
      </div>
      <span className="text-[10px] font-medium" style={{ color }}>{statusLabel(status)}</span>
    </div>
  )
}
